import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { formatTime } from '../utils/quizUtils';
import QuestionImage from '../../QuestionImage';

const IMAGE_CACHE = "question-images-v1";

export const QuizStartScreen = ({ questions, totalQuizTimeInSeconds, subjectName, handleStartQuiz }) => {
    const [downloaded, setDownloaded] = useState(0);
    const [downloading, setDownloading] = useState(false);
    const [complete, setComplete] = useState(false);
    const [error, setError] = useState("");

    // 1. Extract and map dynamic image list safely
    const dynamicImageList = useMemo(() => {
        return questions
            .map(q => {
                if (!q.question_img || q.question_img.trim() === "") return null;
                let imgPath = q.question_img.trim();
                if (!imgPath.startsWith("http") && !imgPath.startsWith("/")) {
                    imgPath = `/questionsPhoto/${imgPath}`;
                }
                return imgPath;
            })
            .filter(Boolean);
    }, [questions]);

    // 2. Download images into Cache Storage automatically
    const downloadImages = useCallback(async (imagesToDownload) => {
        if (imagesToDownload.length === 0) {
            setComplete(true);
            return;
        }

        setDownloading(true);
        setComplete(false);
        setError("");
        setDownloaded(0);

        try {
            const cache = await caches.open(IMAGE_CACHE);
            let count = 0;

            for (const imageUrl of imagesToDownload) {
                const alreadyCached = await cache.match(imageUrl);

                if (!alreadyCached) {
                    const response = await fetch(imageUrl);

                    if (!response.ok) {
                        throw new Error(`Unable to download: ${imageUrl}`);
                    }
                    await cache.put(imageUrl, response.clone());
                }
                count++;
                setDownloaded(count);
            }

            setComplete(true);
        } catch (err) {
            console.error(err);
            setError("Unable to download question images. Please check your connection and try again.");
        } finally {
            setDownloading(false);
        }
    }, []);

    // 3. Automatically trigger download on mount
    useEffect(() => {
        if (questions.length > 0) {
            if (dynamicImageList.length > 0) {
                downloadImages(dynamicImageList);
            } else {
                setComplete(true);
            }
        }
    }, [questions.length, dynamicImageList, downloadImages]);

    const percentage = dynamicImageList.length === 0 ? 100 : Math.round((downloaded / dynamicImageList.length) * 100);

    return (
        <div className="quiz-start-container">
            <h2 className="quiz-start-header">Mock Test Ready!</h2>
            
            <div className="quiz-info-container">
                <div className="quiz-info-item">
                    <div className="quiz-info-label">Total Questions</div>
                    <div className="quiz-info-value">{questions.length}</div>
                </div>
                <div className="quiz-info-item">
                    <div className="quiz-info-label">Total Time</div>
                    <div className="quiz-info-value">{formatTime(totalQuizTimeInSeconds)}</div>
                </div>
            </div>

            <h3>{subjectName} Mock Test</h3>

           

            {/* Progress Bar during download */}
            {downloading && (
                <div className="quiz-progress-box" style={{ margin: "20px 0" }}>
                    <p>Preparing Mock Test ({percentage}%)</p>
                    <div className="qil-progress-bar-container" style={{ width: "100%", background: "#eee", height: "15px", borderRadius: "4px" }}>
                        <div className="qil-progress-bar-fill" style={{ width: `${percentage}%`, background: "#4f46e5", height: "100%", borderRadius: "4px" }}></div>
                    </div>
                </div>
            )}

            {/* Error & Retry Option */}
            {error && (
                <div style={{ margin: "20px 0" }}>
                    <p style={{ color: "red" }}>{error}</p>
                    <button
                        className="retry-btn"
                        onClick={() => downloadImages(dynamicImageList)}
                        style={{ padding: "8px 16px", cursor: "pointer" }}
                    >
                        Retry Download
                    </button>
                </div>
            )}

       
            <button
                className="start-quiz-btn"
                onClick={handleStartQuiz}
                disabled={!complete}
                style={{
                    opacity: complete ? 1 : 0.6,
                    cursor: complete ? "pointer" : "not-allowed",
                    marginTop: "20px"
                }}
            >
                <span className="start-quiz-icon">▶</span>
                {complete ? "Start mock test ➔" : "Please Wait..."}
            </button>
        </div>
    );
};